/**
 * Locale settings for the partner-shop storefront.
 *
 * `locales` is the full **registry** of confirmed target locales (DEC-01/D-10);
 * the set that is actually reachable is decided by the process environment and
 * resolved by `resolveEnabledLocales` in this file and by the server-only
 * accessor in `src/lib/locale/enabled.server.ts`, which is the only module that
 * names the gate variable (D-01/D-02).
 *
 * The existing thirteen public locales keep their explicit runtime activation.
 * Thirteen additional target locales remain behind the same gate until accepted.
 *
 * This module MUST stay pure: it is imported by 116 files, several of them
 * client components, so it may never read `process.env` (RESEARCH Pitfall 1).
 *
 * Translations live in `src/messages/<locale>.json`.
 * Currency comes from the Saleor channel, not here.
 */

import { LanguageCodeEnum } from "@/gql/graphql";

// locale-literals-ok: AGENTS.md — the single locale registry every other list derives from (D-01/D-02); deriving this list would be circular.
export const locales = [
	"de",
	"en",
	"es",
	"tr",
	"fr",
	"it",
	"nl",
	"pl",
	"pt",
	"sv",
	"da",
	"cs",
	"nb",
	"et",
	"fi",
	"ga",
	"el",
	"hr",
	"lv",
	"mt",
	"ro",
	"sk",
	"sl",
	"hu",
	"bg",
	"lt",
] as const;
export type Locale = (typeof locales)[number];
/**
 * Sales-country locales from the 2026-10-21 content rollout. An integrated
 * supplemental store must be complete (a missing reviewed string is an error,
 * never another language); without one the registry fallback chain applies.
 * `ga` and `mt` come from the separate 24-language rollout and use the chain.
 */
// locale-literals-ok: docs/plans/2026-10-21-verkaufslaender-sprachen-seo-geo.md — explicit additive rollout scope, independent of the public gate.
export const salesCountryLocales = [
	"hr",
	"el",
	"hu",
	"sk",
	"bg",
	"sl",
	"ro",
	"fi",
	"lv",
	"et",
	"lt",
] as const satisfies readonly Locale[];
export type SalesCountryLocale = (typeof salesCountryLocales)[number];

export function isSalesCountryLocale(locale: string | undefined | null): locale is SalesCountryLocale {
	return typeof locale === "string" && (salesCountryLocales as readonly string[]).includes(locale);
}

/** Locale-specific supplementary UI introduced with Bokmål and required by subsequent rollouts. */
export function hasSupplementalUiCopy(locale: string | undefined | null): boolean {
	return locale === "nb" || isSalesCountryLocale(locale);
}

export const defaultLocale: Locale = "de";
export const DEFAULT_LOCALE: Locale = defaultLocale;
export const LOCALES = locales;

/**
 * The documented default enabled set — applied when the gate value is absent
 * or blank, and to unknown-only values so a typo can never disable the whole
 * storefront. See the accessor in `src/lib/locale/enabled.server.ts` for the
 * variable that carries the configured value.
 */
// locale-literals-ok: AGENTS.md — the registry's documented default enabled set (D-01); ENABLED_LOCALES overrides it, so it cannot be derived from the known locales.
const DEFAULT_ENABLED_SET = ["de", "en", "es", "tr"] as const satisfies readonly Locale[];

export interface LocaleConfig {
	/** BCP 47 locale for Intl APIs (number/date formatting) */
	bcp47: string;
	/** Saleor GraphQL LanguageCodeEnum value */
	saleorLanguageCode: LanguageCodeEnum;
	/** HTML lang attribute */
	htmlLang: string;
	/** Open Graph locale */
	ogLocale: string;
	/** Native name for language switcher */
	nativeName: string;
	/** Fallback currency when API returns null */
	fallbackCurrency: string;
	/** hreflang / `inLanguage` code — the SEO authority (DEC-02: `pt` is `pt-PT`) */
	hreflangCode: string;
	/**
	 * Default content chain to the reference language (`de`), first hop = the
	 * locale itself. The single fallback policy that replaces the per-store
	 * chains (D-03); consumed by `src/lib/locale/content-chain.ts`.
	 */
	contentFallbackChain: readonly Locale[];
}

/**
 * The exhaustive per-locale table. `Record<Locale, LocaleConfig>` is the
 * compile-time forcing function: a new registry locale without a row here is a
 * type error, and a row without a registry locale is an excess-property error.
 * Prefer `getLocaleConfig(locale)` for lookups; the table itself is exported so
 * the registry contract can be asserted key-for-key in tests.
 */
export const localeConfigs: Record<Locale, LocaleConfig> = {
	de: {
		bcp47: "de-DE",
		saleorLanguageCode: LanguageCodeEnum.De,
		htmlLang: "de",
		ogLocale: "de_DE",
		nativeName: "Deutsch",
		fallbackCurrency: "EUR",
		hreflangCode: "de",
		// locale-literals-ok: AGENTS.md — per-locale chain in the exhaustive Record<Locale, LocaleConfig>; the type forces a row (D-03).
		contentFallbackChain: ["de"],
	},
	en: {
		bcp47: "en-US",
		saleorLanguageCode: LanguageCodeEnum.En,
		htmlLang: "en",
		ogLocale: "en_US",
		nativeName: "English",
		fallbackCurrency: "EUR",
		hreflangCode: "en",
		// locale-literals-ok: AGENTS.md — per-locale chain in the exhaustive Record<Locale, LocaleConfig>; the type forces a row (D-03).
		contentFallbackChain: ["en", "de"],
	},
	es: {
		bcp47: "es-ES",
		saleorLanguageCode: LanguageCodeEnum.Es,
		htmlLang: "es",
		ogLocale: "es_ES",
		nativeName: "Español",
		fallbackCurrency: "EUR",
		hreflangCode: "es",
		// locale-literals-ok: AGENTS.md — per-locale chain in the exhaustive Record<Locale, LocaleConfig>; the type forces a row (D-03).
		contentFallbackChain: ["es", "de"],
	},
	tr: {
		bcp47: "tr-TR",
		saleorLanguageCode: LanguageCodeEnum.Tr,
		htmlLang: "tr",
		ogLocale: "tr_TR",
		nativeName: "Türkçe",
		fallbackCurrency: "EUR",
		hreflangCode: "tr",
		// locale-literals-ok: AGENTS.md — per-locale chain in the exhaustive Record<Locale, LocaleConfig>; the type forces a row (D-03).
		contentFallbackChain: ["tr", "de"],
	},
	fr: {
		bcp47: "fr-FR",
		saleorLanguageCode: LanguageCodeEnum.Fr,
		htmlLang: "fr",
		ogLocale: "fr_FR",
		nativeName: "Français",
		fallbackCurrency: "EUR",
		hreflangCode: "fr",
		// locale-literals-ok: AGENTS.md — per-locale chain in the exhaustive Record<Locale, LocaleConfig>; the type forces a row (D-03).
		contentFallbackChain: ["fr", "de"],
	},
	it: {
		bcp47: "it-IT",
		saleorLanguageCode: LanguageCodeEnum.It,
		htmlLang: "it",
		ogLocale: "it_IT",
		nativeName: "Italiano",
		fallbackCurrency: "EUR",
		hreflangCode: "it",
		// locale-literals-ok: AGENTS.md — per-locale chain in the exhaustive Record<Locale, LocaleConfig>; the type forces a row (D-03).
		contentFallbackChain: ["it", "de"],
	},
	nl: {
		bcp47: "nl-NL",
		saleorLanguageCode: LanguageCodeEnum.Nl,
		htmlLang: "nl",
		ogLocale: "nl_NL",
		nativeName: "Nederlands",
		fallbackCurrency: "EUR",
		hreflangCode: "nl",
		// locale-literals-ok: AGENTS.md — per-locale chain in the exhaustive Record<Locale, LocaleConfig>; the type forces a row (D-03).
		contentFallbackChain: ["nl", "de"],
	},
	pl: {
		bcp47: "pl-PL",
		saleorLanguageCode: LanguageCodeEnum.Pl,
		htmlLang: "pl",
		ogLocale: "pl_PL",
		nativeName: "Polski",
		fallbackCurrency: "EUR",
		hreflangCode: "pl",
		// locale-literals-ok: AGENTS.md — per-locale chain in the exhaustive Record<Locale, LocaleConfig>; the type forces a row (D-03).
		contentFallbackChain: ["pl", "de"],
	},
	pt: {
		bcp47: "pt-PT",
		saleorLanguageCode: LanguageCodeEnum.Pt,
		htmlLang: "pt-PT",
		ogLocale: "pt_PT",
		nativeName: "Português",
		fallbackCurrency: "EUR",
		hreflangCode: "pt-PT",
		// locale-literals-ok: AGENTS.md — per-locale chain in the exhaustive Record<Locale, LocaleConfig>; the type forces a row (D-03).
		contentFallbackChain: ["pt", "de"],
	},
	sv: {
		bcp47: "sv-SE",
		saleorLanguageCode: LanguageCodeEnum.Sv,
		htmlLang: "sv",
		ogLocale: "sv_SE",
		nativeName: "Svenska",
		fallbackCurrency: "EUR",
		hreflangCode: "sv",
		// locale-literals-ok: AGENTS.md — per-locale chain in the exhaustive Record<Locale, LocaleConfig>; the type forces a row (D-03).
		contentFallbackChain: ["sv", "de"],
	},
	da: {
		bcp47: "da-DK",
		saleorLanguageCode: LanguageCodeEnum.Da,
		htmlLang: "da",
		ogLocale: "da_DK",
		nativeName: "Dansk",
		fallbackCurrency: "EUR",
		hreflangCode: "da",
		// locale-literals-ok: AGENTS.md — per-locale chain in the exhaustive Record<Locale, LocaleConfig>; the type forces a row (D-03).
		contentFallbackChain: ["da", "de"],
	},
	cs: {
		bcp47: "cs-CZ",
		saleorLanguageCode: LanguageCodeEnum.Cs,
		htmlLang: "cs",
		ogLocale: "cs_CZ",
		nativeName: "Čeština",
		fallbackCurrency: "EUR",
		hreflangCode: "cs",
		// locale-literals-ok: AGENTS.md — per-locale chain in the exhaustive Record<Locale, LocaleConfig>; the type forces a row (D-03).
		contentFallbackChain: ["cs", "de"],
	},
	nb: {
		bcp47: "nb-NO",
		saleorLanguageCode: LanguageCodeEnum.Nb,
		htmlLang: "nb",
		ogLocale: "nb_NO",
		nativeName: "Norsk bokmål",
		fallbackCurrency: "EUR",
		hreflangCode: "nb",
		// locale-literals-ok: AGENTS.md — per-locale chain in the exhaustive Record<Locale, LocaleConfig>; the type forces a row (D-03).
		contentFallbackChain: ["nb", "de"],
	},
	// Native names below are Intl.DisplayNames metadata (Node 24), not authored copy.
	et: {
		bcp47: "et-EE",
		saleorLanguageCode: LanguageCodeEnum.Et,
		htmlLang: "et",
		ogLocale: "et_EE",
		nativeName: "eesti",
		fallbackCurrency: "EUR",
		hreflangCode: "et",
		// locale-literals-ok: AGENTS.md — per-locale chain in the exhaustive Record<Locale, LocaleConfig>; the type forces a row (D-03).
		contentFallbackChain: ["et", "de"],
	},
	fi: {
		bcp47: "fi-FI",
		saleorLanguageCode: LanguageCodeEnum.Fi,
		htmlLang: "fi",
		ogLocale: "fi_FI",
		nativeName: "suomi",
		fallbackCurrency: "EUR",
		hreflangCode: "fi",
		// locale-literals-ok: AGENTS.md — per-locale chain in the exhaustive Record<Locale, LocaleConfig>; the type forces a row (D-03).
		contentFallbackChain: ["fi", "de"],
	},
	ga: {
		bcp47: "ga-IE",
		saleorLanguageCode: LanguageCodeEnum.Ga,
		htmlLang: "ga",
		ogLocale: "ga_IE",
		nativeName: "Gaeilge",
		fallbackCurrency: "EUR",
		hreflangCode: "ga",
		// locale-literals-ok: AGENTS.md — per-locale chain in the exhaustive Record<Locale, LocaleConfig>; the type forces a row (D-03).
		contentFallbackChain: ["ga", "de"],
	},
	el: {
		bcp47: "el-GR",
		saleorLanguageCode: LanguageCodeEnum.El,
		htmlLang: "el",
		ogLocale: "el_GR",
		nativeName: "Ελληνικά",
		fallbackCurrency: "EUR",
		hreflangCode: "el",
		// locale-literals-ok: AGENTS.md — per-locale chain in the exhaustive Record<Locale, LocaleConfig>; the type forces a row (D-03).
		contentFallbackChain: ["el", "de"],
	},
	hr: {
		bcp47: "hr-HR",
		saleorLanguageCode: LanguageCodeEnum.Hr,
		htmlLang: "hr",
		ogLocale: "hr_HR",
		nativeName: "hrvatski",
		fallbackCurrency: "EUR",
		hreflangCode: "hr",
		// locale-literals-ok: AGENTS.md — per-locale chain in the exhaustive Record<Locale, LocaleConfig>; the type forces a row (D-03).
		contentFallbackChain: ["hr", "de"],
	},
	lv: {
		bcp47: "lv-LV",
		saleorLanguageCode: LanguageCodeEnum.Lv,
		htmlLang: "lv",
		ogLocale: "lv_LV",
		nativeName: "latviešu",
		fallbackCurrency: "EUR",
		hreflangCode: "lv",
		// locale-literals-ok: AGENTS.md — per-locale chain in the exhaustive Record<Locale, LocaleConfig>; the type forces a row (D-03).
		contentFallbackChain: ["lv", "de"],
	},
	mt: {
		bcp47: "mt-MT",
		saleorLanguageCode: LanguageCodeEnum.Mt,
		htmlLang: "mt",
		ogLocale: "mt_MT",
		nativeName: "Malti",
		fallbackCurrency: "EUR",
		hreflangCode: "mt",
		// locale-literals-ok: AGENTS.md — per-locale chain in the exhaustive Record<Locale, LocaleConfig>; the type forces a row (D-03).
		contentFallbackChain: ["mt", "de"],
	},
	ro: {
		bcp47: "ro-RO",
		saleorLanguageCode: LanguageCodeEnum.Ro,
		htmlLang: "ro",
		ogLocale: "ro_RO",
		nativeName: "română",
		fallbackCurrency: "EUR",
		hreflangCode: "ro",
		// locale-literals-ok: AGENTS.md — per-locale chain in the exhaustive Record<Locale, LocaleConfig>; the type forces a row (D-03).
		contentFallbackChain: ["ro", "de"],
	},
	sk: {
		bcp47: "sk-SK",
		saleorLanguageCode: LanguageCodeEnum.Sk,
		htmlLang: "sk",
		ogLocale: "sk_SK",
		nativeName: "slovenčina",
		fallbackCurrency: "EUR",
		hreflangCode: "sk",
		// locale-literals-ok: AGENTS.md — per-locale chain in the exhaustive Record<Locale, LocaleConfig>; the type forces a row (D-03).
		contentFallbackChain: ["sk", "de"],
	},
	sl: {
		bcp47: "sl-SI",
		saleorLanguageCode: LanguageCodeEnum.Sl,
		htmlLang: "sl",
		ogLocale: "sl_SI",
		nativeName: "slovenščina",
		fallbackCurrency: "EUR",
		hreflangCode: "sl",
		// locale-literals-ok: AGENTS.md — per-locale chain in the exhaustive Record<Locale, LocaleConfig>; the type forces a row (D-03).
		contentFallbackChain: ["sl", "de"],
	},
	hu: {
		bcp47: "hu-HU",
		saleorLanguageCode: LanguageCodeEnum.Hu,
		htmlLang: "hu",
		ogLocale: "hu_HU",
		nativeName: "magyar",
		fallbackCurrency: "EUR",
		hreflangCode: "hu",
		// locale-literals-ok: AGENTS.md — per-locale chain in the exhaustive Record<Locale, LocaleConfig>; the type forces a row (D-03).
		contentFallbackChain: ["hu", "de"],
	},
	bg: {
		bcp47: "bg-BG",
		saleorLanguageCode: LanguageCodeEnum.Bg,
		htmlLang: "bg",
		ogLocale: "bg_BG",
		nativeName: "български",
		fallbackCurrency: "EUR",
		hreflangCode: "bg",
		// locale-literals-ok: AGENTS.md — per-locale chain in the exhaustive Record<Locale, LocaleConfig>; the type forces a row (D-03).
		contentFallbackChain: ["bg", "de"],
	},
	lt: {
		bcp47: "lt-LT",
		saleorLanguageCode: LanguageCodeEnum.Lt,
		htmlLang: "lt",
		ogLocale: "lt_LT",
		nativeName: "lietuvių",
		fallbackCurrency: "EUR",
		hreflangCode: "lt",
		// locale-literals-ok: AGENTS.md — per-locale chain in the exhaustive Record<Locale, LocaleConfig>; the type forces a row (D-03).
		contentFallbackChain: ["lt", "de"],
	},
};

/**
 * Get locale configuration by locale code.
 * Falls back to default locale if invalid.
 */
export function getLocaleConfig(locale: string): LocaleConfig {
	if (locale in localeConfigs) {
		return localeConfigs[locale as Locale];
	}
	return localeConfigs[defaultLocale];
}

/** Check if a string is a valid locale */
export function isValidLocale(value: string): value is Locale {
	return locales.includes(value as Locale);
}

/**
 * Parse the enable-gate value into the enabled locale list.
 *
 * Pure and total: the caller supplies the raw value, this function never reads
 * process state and never throws (the shape `parseGeoPrefCookieValue` uses).
 * The accessor in `src/lib/locale/enabled.server.ts` is the only caller that
 * reads the value from the environment.
 *
 * Rules, in order:
 *
 * - absent or blank → the documented default set;
 * - otherwise comma-separated tokens, trimmed and lowercased, unknown codes
 *   ignored with exactly one `[locale]` warning naming them;
 * - the result is the **registry order**, never the environment order;
 * - the default locale is always included, because the unprefixed German root
 *   must stay reachable;
 * - an unknown-only value returns the default set, never an empty list.
 */
export function resolveEnabledLocales(raw: string | undefined): readonly Locale[] {
	if (raw === undefined || raw.trim() === "") {
		return DEFAULT_ENABLED_SET;
	}
	const seen = new Set<Locale>();
	const unknown: string[] = [];
	for (const token of raw
		.split(",")
		.map((t) => t.trim().toLowerCase())
		.filter(Boolean)) {
		if (isValidLocale(token)) {
			seen.add(token);
		} else {
			unknown.push(token);
		}
	}
	if (unknown.length > 0) {
		console.warn(`[locale] Enable gate ignored unknown codes: ${unknown.join(", ")}`);
	}
	if (seen.size === 0) {
		return DEFAULT_ENABLED_SET;
	}
	seen.add(defaultLocale);
	return locales.filter((locale) => seen.has(locale));
}

/**
 * Known channels for hreflang generation in HTML metadata and sitemaps.
 * Single-channel partner shop, value kept for API compatibility.
 */
export const sitemapChannels = ["standard"] as const;
export type SitemapChannel = (typeof sitemapChannels)[number];
export const defaultSitemapChannel: SitemapChannel = "standard";

/**
 * Primary SEO target per locale.
 *
 * Single-locale partner shop, kept for API compatibility.
 */
export const primaryHreflangChannelByLocale: Record<Locale, SitemapChannel> = {
	// locale-literals-ok: AGENTS.md — exhaustive Record<Locale, SitemapChannel>; the type system is the gate, so no derivation is needed or wanted.
	de: "standard",
	en: "standard",
	es: "standard",
	tr: "standard",
	fr: "standard",
	it: "standard",
	nl: "standard",
	pl: "standard",
	pt: "standard",
	sv: "standard",
	da: "standard",
	cs: "standard",
	nb: "standard",
	et: "standard",
	fi: "standard",
	ga: "standard",
	el: "standard",
	hr: "standard",
	lv: "standard",
	mt: "standard",
	ro: "standard",
	sk: "standard",
	sl: "standard",
	hu: "standard",
	// locale-literals-ok: AGENTS.md — bg/lt rows of the exhaustive Record<Locale, …> above; the sweep caps one multi-line map at 24 entries, so the type system stays the gate.
	bg: "standard",
	lt: "standard",
};

export function getPrimaryHreflangChannel(locale: Locale): SitemapChannel {
	return primaryHreflangChannelByLocale[locale];
}

/**
 * Get the hreflang code for a locale+channel combination.
 *
 * The channel argument is kept for API compatibility and cannot change the
 * result: the registry's `hreflangCode` is the single authority (DEC-02 —
 * `pt` renders as `pt-PT`, every other locale equals its locale code).
 */
export function getHreflangCode(locale: Locale, channel: string): string {
	void channel;
	return getLocaleConfig(locale).hreflangCode;
}

/**
 * Normalize Unicode whitespace from Intl.NumberFormat output.
 *
 * Node.js and browsers use different whitespace between number and currency symbol:
 * - Node.js: NBSP ( )
 * - iOS Safari 17+: NNBSP ( , narrow no-break space)
 *
 * This mismatch causes React hydration errors (#418 text). Normalizing to NBSP
 * ensures consistent output and preserves the no-break behavior.
 */
function normalizeIntlWhitespace(str: string): string {
	return str.replace(/ /g, " ");
}

/**
 * Normalize a Saleor / API currency code for Intl (must be 3 letters; empty or invalid → fallback).
 */
function resolveCurrencyForIntl(raw: string | null | undefined, fallback: string): string {
	if (typeof raw !== "string") return fallback;
	const trimmed = raw.trim();
	return /^[A-Za-z]{3}$/.test(trimmed) ? trimmed.toUpperCase() : fallback;
}

/**
 * Format a price with the given locale.
 *
 * Saleor should always send a valid ISO 4217 code; when it does not, `Intl.NumberFormat`
 * throws (e.g. empty string) and would collapse entire client subtrees behind an error boundary.
 */
export function formatPrice(amount: number, currency: string, locale: Locale = defaultLocale): string {
	const config = getLocaleConfig(locale);
	const safeAmount = Number.isFinite(amount) ? amount : 0;
	const code = resolveCurrencyForIntl(currency, config.fallbackCurrency);
	try {
		return normalizeIntlWhitespace(
			new Intl.NumberFormat(config.bcp47, {
				style: "currency",
				currency: code,
			}).format(safeAmount),
		);
	} catch {
		return `${safeAmount} ${code}`;
	}
}

/**
 * Format a date with the given locale.
 */
export function formatDate(
	date: Date | number,
	options?: Intl.DateTimeFormatOptions,
	locale: Locale = defaultLocale,
): string {
	return new Intl.DateTimeFormat(getLocaleConfig(locale).bcp47, {
		dateStyle: "medium",
		...options,
	}).format(date);
}

/**
 * Format a number with the given locale.
 */
export function formatNumber(
	value: number,
	options?: Intl.NumberFormatOptions,
	locale: Locale = defaultLocale,
): string {
	return normalizeIntlWhitespace(new Intl.NumberFormat(getLocaleConfig(locale).bcp47, options).format(value));
}

/**
 * @deprecated Use getLocaleConfig(locale) instead. Kept for backward compatibility.
 */
export const localeConfig = {
	default: localeConfigs[defaultLocale].bcp47,
	graphqlLanguageCode: localeConfigs[defaultLocale].saleorLanguageCode,
	htmlLang: localeConfigs[defaultLocale].htmlLang,
	ogLocale: localeConfigs[defaultLocale].ogLocale,
	available: ["de-DE", "en-US", "es-ES", "tr-TR"] as const,
	fallbackCurrency: localeConfigs[defaultLocale].fallbackCurrency,
} as const;
